import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchState } from '@/hooks/useSearchState';
import { Search, FileText, CheckSquare, Users, X, ArrowRight, Loader2 } from 'lucide-react';

// ─── Sub-components ───────────────────────────────────────────────────────────
function ResultGroup({ title, items, icon: Icon, children }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 px-4 py-1.5 mb-1">
        <Icon size={11} style={{ color: '#aaa' }} />
        <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#aaa' }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

function ResultItem({ primary, secondary, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/60 group transition-colors text-left"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{primary}</p>
        {secondary && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{secondary}</p>
        )}
      </div>
      {badge && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0"
          style={{ background: 'rgba(105,148,198,0.12)', color: '#6994c6' }}
        >
          {badge}
        </span>
      )}
      <ArrowRight size={12} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { query, results, loading, handleChange, reset, totalResults } = useSearchState(open);

  // Focus input when opened; reset when closed
  useEffect(() => {
    if (open) {
      reset();
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, reset]);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100]"
        style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed z-[101] left-1/2 -translate-x-1/2"
        style={{ top: '15vh', width: '100%', maxWidth: '560px', padding: '0 16px' }}
      >
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: '#fff',
            border: '1px solid hsl(var(--border))',
            boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
          }}
        >
          {/* Input row */}
          <div
            className="flex items-center gap-3 px-4 py-3.5"
            style={{ borderBottom: '1px solid hsl(var(--border))' }}
          >
            {loading
              ? <Loader2 size={16} className="text-muted-foreground animate-spin shrink-0" />
              : <Search size={16} className="text-muted-foreground shrink-0" />}
            <input
              ref={inputRef}
              data-testid="global-search-input"
              value={query}
              onChange={e => handleChange(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && onClose()}
              placeholder="Search actions, tasks, people…"
              className="flex-1 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            />
            {query && (
              <button onClick={() => { reset(); inputRef.current?.focus(); }}>
                <X size={14} className="text-muted-foreground hover:text-foreground" />
              </button>
            )}
            <kbd
              className="text-[10px] px-1.5 py-0.5 rounded border font-mono shrink-0"
              style={{ borderColor: 'hsl(var(--border))', color: '#aaa', background: 'hsl(var(--secondary))' }}
            >
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="py-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
            {!query && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Type to search across actions, tasks, and people
              </p>
            )}
            {query && !loading && results && totalResults === 0 && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                No results for &ldquo;<strong>{query}</strong>&rdquo;
              </p>
            )}
            {results && (
              <>
                <ResultGroup title="Action Cards" icon={FileText} items={results.action_cards}>
                  {results.action_cards.map(card => (
                    <ResultItem
                      key={card.id}
                      primary={card.title || card.contact_name}
                      secondary={card.summary_bullets?.[0] || card.contact_name}
                      badge={card.status}
                      onClick={() => go(`/action-cards/${card.id}`)}
                    />
                  ))}
                </ResultGroup>
                <ResultGroup title="Tasks" icon={CheckSquare} items={results.tasks}>
                  {results.tasks.map(task => (
                    <ResultItem
                      key={task.id}
                      primary={task.title}
                      secondary={task.action_card_title || task.owner}
                      badge={task.status}
                      onClick={() => go(`/action-cards/${task.action_card_id}`)}
                    />
                  ))}
                </ResultGroup>
                <ResultGroup title="Contacts" icon={Users} items={results.contacts}>
                  {results.contacts.map(contact => (
                    <ResultItem
                      key={contact.id}
                      primary={contact.name}
                      secondary={contact.email || contact.company}
                      badge={contact.action_card_ids?.length ? `${contact.action_card_ids.length} cards` : null}
                      onClick={() => go(`/contacts/${contact.id}`)}
                    />
                  ))}
                </ResultGroup>
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex items-center gap-4 px-4 py-2.5 text-[10px] text-muted-foreground"
            style={{ borderTop: '1px solid hsl(var(--border))', background: 'hsl(var(--secondary)/0.4)' }}
          >
            <span><kbd className="font-mono">⌘K</kbd> to open</span>
            <span><kbd className="font-mono">ESC</kbd> to close</span>
          </div>
        </div>
      </div>
    </>
  );
}
